'use client';
import base, { AxiosInstance } from 'axios';
import { createContext, useContext, useMemo, useState } from 'react';
import { AuthContext, clearStorage } from './Authentication';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { getMainApiOrigin, getSelfOriginForRedirects } from '@/config/apiConfig';
import { getActiveTenantHeaders, resolveActiveRequestUserId } from '@/lib/activeTenantScope';

export const AxiosContext = createContext<AxiosInstance>(base.create());

export const isCancel = base.isCancel;

export function extractApiMessage(data: any): string | null {
  if (!data) return null;
  if (typeof data.message === 'string' && data.message) return data.message;
  const detail = data.detail;
  if (!detail) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d: any) => {
      const field = Array.isArray(d.loc)
        ? d.loc.filter((l: any) => typeof l === 'string' && l !== 'body').join('.')
        : '';
      const msg = d.msg || 'Validation error';
      return field ? `${field}: ${msg}` : msg;
    }).join('; ');
  }
  return null;
}

function generateErrorHash(status: number, message: string): string {
  const str = `${status}:${message}`;
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).slice(0, 6).toUpperCase();
}

function getInstance(auth, setSessionExpiredOpen) {
  const mainOrigin = getMainApiOrigin();
  const axios = base.create({
    baseURL: `${mainOrigin}/api/`,
    withCredentials: true,
  });

  const auth_exclude: Array<string> = [];

  axios.interceptors.request.use((req) => {
    const activeUserId = resolveActiveRequestUserId(auth.userId);
    if (!req.data) {
      req.data = {};
    }
    if (!req.data.userID && activeUserId) {
      if (req.data instanceof FormData) {
        req.data.append('userID', String(activeUserId));
      } else {
        req.data.userID = activeUserId;
      }
    }
    if (auth.isAuthenticated && auth_exclude.indexOf(req.url!) == -1) {
      if (!req.data.session_token) {
        req.data.session_token = auth.token;
      }
    }

    if (
      req.url &&
      (!req.url.startsWith('http') || req.url.startsWith(mainOrigin))
    ) {
      req.headers['cookies'] = document.cookie;
    }
    req.headers['Accept'] = 'application/json';
    req.headers['X-Frontend-Page'] = window.location.href;
    Object.assign(req.headers, getActiveTenantHeaders());
    if (activeUserId) {
      req.headers['X-User-ID'] = String(activeUserId);
    }

    return req;
  });

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (window.navigator.onLine && err.response) {
        if (err.response.status == 401) {
          if (
            auth.isAuthenticated &&
            err.response.config.url != `${mainOrigin}/api/logout`
          ) {
            // alert('Session expired! Please log in again.');
            // auth.logout();
            location.href = `${getSelfOriginForRedirects()}/v2/login`;
          } else if (err.response.config.url == `${mainOrigin}/api/userLogin`) {
            location.href = `${getSelfOriginForRedirects()}/account-disabled`;
          } else {
            setSessionExpiredOpen(true);
          }
        } else {
          const status = err.response.status;
          const apiMessage = extractApiMessage(err.response?.data);
          const hash = generateErrorHash(status, apiMessage || String(status));
          err.errorCode = status;
          err.errorHash = hash;
          err.errorRef = `ERR-${status}-${hash}`;
          const isProd = process.env.NODE_ENV === 'production';
          const safeMessage = (isProd && status >= 500)
            ? null
            : apiMessage;
          err.formattedError = safeMessage || `Request failed with status ${status}`;
        }
      } else {
        err.formattedError =
          'An error occurred while connecting to the network';
      }
      return Promise.reject(err);
    }
  );
  return axios;
}

export default function Axios({ children }) {
  const authData = useContext(AuthContext);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  // useMemo, not useState+useEffect: the effect version rendered the whole app once with a
  // null client and then again with the real one on every page load.
  const client = useMemo(() => getInstance(authData || {}, setSessionExpiredOpen), [authData]);

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    clearStorage();
    location.href = `${getSelfOriginForRedirects()}/v2/login`;
  };

  return (
    <>
      <AxiosContext.Provider value={client}>{children}</AxiosContext.Provider>

      {/* Session Expired Dialog */}
      <Dialog
        open={sessionExpiredOpen}
        onClose={handleSessionExpiredClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">
              Session Expired
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your session has expired. Please log in again to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSessionExpiredClose}
            variant="contained"
            color="primary"
            fullWidth
          >
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
